import { useState, useEffect } from 'react';
import { Lock, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API, toFullUrl } from '../config/api';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';

/**
 * Global modal that appears when a snippet-mode track hits its 30-second limit.
 * Offers one-click Stripe checkout to unlock the full song.
 */
export function UnlockFullSongModal() {
  const { snippetUnlockTrack, dismissUnlock, markUnlocked } = usePlayer();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const track = snippetUnlockTrack;

  // Escape key closes the modal — and ALL other open modals/overlays in the app
  useEffect(() => {
    if (!track) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') dismissUnlock(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [track, dismissUnlock]);

  if (!track) return null;

  const price = Number(track.unlock_price) || 0;
  const itemType = track.unlock_item_type || 'track';

  const handleUnlock = async () => {
    if (price <= 0) {
      // No price configured → just mark as unlocked locally (admin chose snippet but no upsell)
      markUnlocked(track.id);
      dismissUnlock();
      toast.success('Continue listening to the full song');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/unlock-snippet`, {
        item_type: itemType,
        item_id: track.id,
        email: email || undefined,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        // Remember intent — when /success is hit we mark unlocked
        try {
          localStorage.setItem('mp_pendingUnlock', JSON.stringify({ track_id: track.id, session_id: data.session_id }));
        } catch (e) { /* ignore */ }
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unlock failed');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-[300] flex items-center justify-center p-4 animate-fade-up"
      data-testid="unlock-modal"
      onClick={dismissUnlock}
    >
      <div className="relative bg-[#0F0F0F] border-2 border-[#DAA520]/50 max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Always-visible close button (top-right) */}
        <button
          onClick={dismissUnlock}
          className="absolute top-3 right-3 z-10 w-9 h-9 bg-black/70 hover:bg-[#FF3B30] text-white flex items-center justify-center transition-colors"
          data-testid="unlock-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Cover */}
        {track.cover_image && (
          <div className="relative h-48 overflow-hidden">
            <img src={toFullUrl(track.cover_image)} alt="" className="w-full h-full object-cover blur-sm opacity-50 scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
            <div className="absolute bottom-4 left-5 right-5">
              <div className="w-14 h-14 bg-[#DAA520] text-black flex items-center justify-center mb-3">
                <Lock className="w-6 h-6" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#DAA520] font-bold">SNIPPET ENDED</p>
            </div>
          </div>
        )}
        <div className="p-6 space-y-4">
          {!track.cover_image && (
            <>
              <div className="w-14 h-14 bg-[#DAA520] text-black flex items-center justify-center">
                <Lock className="w-6 h-6" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#DAA520] font-bold">SNIPPET ENDED</p>
            </>
          )}
          <div>
            <h3 className="font-cabinet text-2xl font-black uppercase tracking-tighter leading-tight">{track.title || 'This Track'}</h3>
            {track.artist && <p className="text-sm text-[#A3A3A3] mt-1">{track.artist}</p>}
          </div>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            You&apos;ve heard the 30-second preview. Unlock the <strong className="text-white">full song</strong> instantly.
          </p>
          {price > 0 ? (
            <>
              <div className="bg-[#141414] border border-[#262626] p-4 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Unlock Price</span>
                <span className="font-cabinet text-3xl font-black text-[#DAA520]">${price.toFixed(2)}</span>
              </div>
              <input
                type="email"
                placeholder="your@email.com (for receipt)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2.5 text-sm font-mono"
                data-testid="unlock-email"
              />
            </>
          ) : (
            <p className="text-xs text-[#737373]">No purchase needed — just confirm to continue.</p>
          )}
          <button
            onClick={handleUnlock}
            disabled={loading}
            className="w-full h-12 bg-[#DAA520] hover:bg-white text-black font-black uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="unlock-submit"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : (
              price > 0 ? <><Lock className="w-4 h-4" /> UNLOCK FULL SONG · ${price.toFixed(2)}</> : 'CONTINUE LISTENING'
            )}
          </button>
          <button onClick={dismissUnlock} className="w-full text-[10px] uppercase tracking-[0.2em] text-[#525252] hover:text-[#A3A3A3] py-1" data-testid="unlock-maybe-later">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
